import { inject, Injectable } from '@angular/core';
import { PrismicDocument } from '@prismicio/client';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { LanguageService } from 'language';
import { CmsService } from 'prismic';
import { SupportArticle, SupportCategory } from './support-types';
import { sortByDisplayOrder } from './support-utils';
import { urlifyTitle } from './urlify-title';

export interface Category {
  categoryDoc?: PrismicDocument<SupportCategory>;
  childrenCategories?: Category[];
  articles: PrismicDocument<SupportArticle>[];
}

@Injectable({
  providedIn: 'root',
})
export class CategoryService {
  public cmsService = inject(CmsService);
  public lang = inject(LanguageService);
  public cms = inject(CmsService);

  public getCategories() {
    return from(this.cms.getByType('category')).pipe(
      map(categories =>
        (categories as PrismicDocument<SupportCategory>[]).filter(category => !category.data.parent?.id)
      )
    );
  }

  public getCategoryPath(category: PrismicDocument<SupportCategory>): Observable<string> {
    if (category.lang !== this.cmsService.languages[this.lang.current]) {
      const correctLangId = category.alternate_languages.find(
        lang => lang.lang === this.cmsService.languages[this.lang.current]
      )?.id;
      return of(`/${this.lang.current}/${correctLangId}`);
    }
    return of(`/${this.lang.current}/${category.id}-${urlifyTitle(category.data.title)}`);
  }

  public getCategoryChain(categoryId: string): Observable<PrismicDocument<SupportCategory>[]> {
    if (!categoryId) return of([]);
    return from(this.cmsService.getById(categoryId)).pipe(
      switchMap(category => {
        return this.getCategoryChain(category.data.parent?.id).pipe(
          map(parentChain => {
            return [...parentChain, category as PrismicDocument<SupportCategory>];
          })
        );
      })
    );
  }

  public async findSubCategories(
    categoryDoc: PrismicDocument<SupportCategory>,
    loadArticles = false
  ): Promise<Category> {
    const category: Category = {
      categoryDoc: categoryDoc,
      articles: [],
      childrenCategories: [],
    };

    if (!categoryDoc) return category;

    const childCategoryDocs: PrismicDocument<SupportCategory>[] = await this.cms.getByContentRelationship(
      'category',
      'parent',
      category.categoryDoc.id
    );

    const categoryList = sortByDisplayOrder(childCategoryDocs).map<Category>(categoryDoc => ({
      categoryDoc,
      articles: [],
      childrenCategories: [],
    }));

    category.childrenCategories = categoryList;

    if (loadArticles) {
      await Promise.all(
        category.childrenCategories.map(async subCategory => {
          const articlesDoc = await this.cms.getByContentRelationship(
            'article',
            'category',
            subCategory.categoryDoc.id
          );
          subCategory.articles = sortByDisplayOrder(articlesDoc);
        })
      );

      const directArticles = await this.cms.getByContentRelationship('article', 'category', category.categoryDoc.id);
      if (directArticles.length > 0) {
        const othersSubcategory: Category = {
          articles: sortByDisplayOrder(directArticles),
        };
        category.childrenCategories.push(othersSubcategory);
      }
    }

    return category;
  }
}
